import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Loader2, Printer, AlertCircle } from 'lucide-react';
import { BillingInfo, PaymentRecord } from '../lib/billing-hooks';
/**
 * InvoicePage — public receipt viewer.
 *
 * URL: /invoice/:paymentId
 *
 * Renders a thermal-printer-friendly comprobante for a given payment.
 *
 * MULTI-TENANT SAFETY: every fetch is keyed by `payment.restaurant_id`
 * (which comes from the payment row itself, not from any URL param or
 * tenant context). The billing info, the restaurant logo, and the
 * mesa label are all loaded with `.eq('restaurant_id', pay.restaurant_id)`
 * or by following its FK ids. No cross-tenant data ever reaches this
 * page.
 */
export function InvoicePage() {
  const {
    paymentId
  } = useParams<{
    paymentId: string;
  }>();
  const [payment, setPayment] = useState<PaymentRecord | null>(null);
  const [info, setInfo] = useState<BillingInfo | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [tableLabel, setTableLabel] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!paymentId) return;
      try {
        // 1) Payment row — authoritative source for restaurant_id
        const {
          data: pay,
          error: payErr
        } = await supabase.from('payments').select('*').eq('id', paymentId).maybeSingle();
        if (payErr) throw payErr;
        if (!pay) throw new Error('Factura no encontrada');
        // 2) Billing info — scoped to this payment's restaurant_id
        const {
          data: billing,
          error: bErr
        } = await supabase.from('restaurant_billing_info').select('*').eq('restaurant_id', pay.restaurant_id).maybeSingle();
        if (bErr) throw bErr;
        if (!billing) throw new Error('Datos del comercio no disponibles');
        // 3) Restaurant logo — scoped to the same restaurant_id. Non-fatal
        //    on failure; we just won't show the logo block.
        let logo: string | null = null;
        try {
          const {
            data: rest
          } = await supabase.from('restaurants').select('logo_url').eq('id', pay.restaurant_id).maybeSingle();
          if (rest && (rest as any).logo_url) {
            logo = (rest as any).logo_url as string;
          }
        } catch {
          logo = null;
        }
        // 4) Mesa label — only when the payment is linked to a table.
        //    `tables` rows have their own restaurant_id, so resolving by
        //    primary key here is tenant-safe by construction.
        let tLabel: string | null = null;
        if (pay.table_id) {
          const {
            data: tbl
          } = await supabase.from('tables').select('table_number, table_name').eq('id', pay.table_id).maybeSingle();
          if (tbl) tLabel = tbl.table_name || `Mesa ${tbl.table_number}`;
        }
        if (mounted) {
          setPayment(pay as PaymentRecord);
          setInfo(billing as BillingInfo);
          setLogoUrl(logo);
          setTableLabel(tLabel);
        }
      } catch (err: any) {
        if (mounted) setError(err.message || 'Error al cargar la factura');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, [paymentId]);
  const fmt = (n: number) => new Intl.NumberFormat('es-CR', {
    style: 'currency',
    currency: 'CRC',
    minimumFractionDigits: 0
  }).format(n);
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100" data-id="element-7126" data-mp="30w5o6rdnida3">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" data-id="element-7127" data-mp="u6hdc2oief13" />
      </div>;
  }
  if (error || !payment || !info) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100 p-6" data-id="element-7128" data-mp="z26do88crzyg">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-lg" data-id="element-7129" data-mp="2v6m8vnowrnzl">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" data-id="element-7130" data-mp="2r74zwka9w8po" />
          <h1 className="text-xl font-black text-gray-900 mb-1" data-id="element-7131" data-mp="2m5xwexf5xvot">
            Factura no disponible
          </h1>
          <p className="text-sm text-gray-500" data-id="element-7132" data-mp="2h6l07m2ejc9k">
            {error || 'No se pudo cargar el documento.'}
          </p>
        </div>
      </div>;
  }
  // ─── Derived display values ───────────────────────────────────────
  const date = new Date(payment.created_at).toLocaleString('es-CR', {
    dateStyle: 'short',
    timeStyle: 'short'
  });
  const consecStr = String(payment.consecutive_number).padStart(10, '0');
  const methodLabel = payment.method === 'cash' ? 'Efectivo' : payment.method === 'sinpe' ? 'SINPE Móvil' : 'Tarjeta';
  const docTitle = info.regime === 'simplificado' ? 'COMPROBANTE DE PAGO' : 'FACTURA ELECTRÓNICA';
  const regimeLabel = info.regime === 'simplificado' ? 'Régimen Simplificado' : info.regime === 'tradicional' ? 'Régimen Tradicional' : info.regime;
  return <div className="min-h-screen bg-gray-100 py-6 px-4 print:bg-white print:p-0" data-id="element-7133" data-mp="1ocohvztofsmp">
      <style data-id="element-7134" data-mp="1tcar7y6dwoq8">{`
        @media print {
          @page { margin: 6mm; size: 80mm auto; }
          body { background: white !important; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="max-w-sm mx-auto" data-id="element-7135" data-mp="10n3vf6ybed8o">
        {/* Action bar (hidden when printing) */}
        <div className="no-print flex gap-2 mb-4" data-id="element-7136" data-mp="15n00l9ir0apt">
          <button onClick={() => window.print()} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-900 text-white font-bold text-sm shadow-lg hover:bg-gray-800 transition-colors" data-id="element-7137" data-mp="15l5c5l89q0gz">
            <Printer className="w-4 h-4" data-id="element-7138" data-mp="hwvzb8t18n1z" />
            Imprimir / Guardar PDF
          </button>
        </div>

        <p className="no-print text-xs text-gray-500 text-center mb-4 leading-relaxed" data-id="element-7139" data-mp="9vy4zf3z73yl">
          💡 En el diálogo de impresión, elige{' '}
          <strong data-id="element-7140" data-mp="4x38xbgzziwj">"Guardar como PDF"</strong> para descargar el comprobante, o
          seleccioná tu impresora térmica de 80&nbsp;mm.
        </p>

        {/* ───────── Receipt body ───────── */}
        <div className="bg-white p-5 shadow-md font-mono text-gray-900 border border-gray-200 print:border-0 print:shadow-none" data-id="element-7141" data-mp="enoghoenfxrl">
          {/* Logo (when available) — matches the physical receipt: small,
           centered, square-ish, with a subtle border for printer
           contrast. Falls back gracefully when logo_url is missing. */}
          {logoUrl && <div className="flex justify-center mb-3" data-id="element-7142" data-mp="3s1t4i7bsz7np">
              <div className="w-20 h-20 rounded-md overflow-hidden border border-gray-200 bg-white flex items-center justify-center" data-id="element-7143" data-mp="3s4fcg82lvp6g">
                <img src={logoUrl} alt={info.business_name} className="w-full h-full object-contain"
            // Hide the image element if it fails to load — the
            // surrounding box still adds visual weight to the header.
            onError={e => {
              ;
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }} data-id="element-7144" data-mp="17cn06pyf77z0" />
              </div>
            </div>}

          {/* Comercial / brand name (large, centered) */}
          <h1 className="text-base font-black text-center tracking-wide mb-0.5" data-id="element-7145" data-mp="22lguh7mn4dzz">
            {info.business_name}
          </h1>

          {/* Legal owner name (only for personas físicas with legal_name
           configured — e.g. Régimen Simplificado). Backwards compatible:
           when null/empty, this entire line is skipped. */}
          {info.legal_name && <p className="text-[11px] text-center font-bold uppercase tracking-wider mb-2" data-id="element-7146" data-mp="2givmmk4a2zvn">
              {info.legal_name}
            </p>}

          {/* Contact / legal block — compact, centered, mirrors the
           physical receipt's "address + phone + email" layout. */}
          <div className="text-[11px] text-center leading-snug mb-3 space-y-0.5" data-id="element-7147" data-mp="1rrzihiqcdlz6">
            <div data-id="element-7148" data-mp="1mvhisx9vcdtp">
              <strong data-id="element-7149" data-mp="1msv5si47l643">
                Céd. {info.legal_id_type === 'fisica' ? '' : 'Jur. '}
                {info.legal_id}
              </strong>
            </div>
            {info.phone && <div data-id="element-7150" data-mp="2fj4ypz072ga0">Tel. {info.phone}</div>}
            {info.address && <div data-id="element-7151" data-mp="2ak0kuv2brudc">{info.address}</div>}
            {info.email && <div className="break-all" data-id="element-7152" data-mp="2aih658wbhrdo">{info.email}</div>}
          </div>

          {/* Document type + consecutive — visually anchored as a "stamp" */}
          <div className="border-y border-dashed border-gray-400 py-2 my-2" data-id="element-7153" data-mp="20n5pum59gc3t">
            <p className="text-[11px] font-black tracking-wider text-center mb-0.5" data-id="element-7154" data-mp="u626m46gwew7">
              {docTitle}
            </p>
            <p className="text-center font-mono text-xs tabular-nums" data-id="element-7155" data-mp="p65z3uyka5bw">
              {consecStr}
              {payment.is_partial && <span className="ml-1 bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded text-[9px] font-bold" data-id="element-7156" data-mp="30shmjx1c94dy">
                  PARCIAL
                </span>}
            </p>
            <p className="text-[10px] text-center text-gray-600 mt-0.5" data-id="element-7157" data-mp="3arfi3jx6ly6g">
              {date}
            </p>
            {tableLabel && <p className="text-[10px] text-center text-gray-700 mt-0.5" data-id="element-7158" data-mp="3kitng8btds7i">
                <strong data-id="element-7159" data-mp="3kkwcap18dfan">{tableLabel}</strong>
              </p>}
          </div>

          {/* Items table — column widths tuned for 80mm thermal paper */}
          <table className="w-full text-[11px] my-2" data-id="element-7160" data-mp="3tkjcj7i84f24">
            <thead data-id="element-7161" data-mp="2coqx8kiyh37">
              <tr className="border-b border-gray-900 uppercase text-[10px]" data-id="element-7162" data-mp="2d6yh1cn0hqf">
                <td className="pb-1 w-8" data-id="element-7163" data-mp="1z5gufuvi7z98">Cant.</td>
                <td className="pb-1" data-id="element-7164" data-mp="1z6s0y2gc3l41">Descripción</td>
                <td className="pb-1 text-right whitespace-nowrap" data-id="element-7165" data-mp="243s2q165i1pc">
                  Monto total
                </td>
              </tr>
            </thead>
            <tbody data-id="element-7166" data-mp="1hysiwa1pvp6s">
              {(payment.items_json || []).map((it, idx) => <tr key={idx} className="align-top" data-id="element-7167" data-mp="1mwkhto0s4bkh" data-mpi={idx}>
                  <td className="font-bold pt-1.5" data-id="element-7168" data-mp="1rvdooxlk94bo">{it.quantity}</td>
                  <td className="pr-2 pt-1.5" data-id="element-7169" data-mp="1rx85q0tpocah">
                    <div className="leading-tight" data-id="element-7170" data-mp="1vwz3iohnnsoi">{it.item_name}</div>
                    {it.notes && <div className="text-[10px] text-gray-500 italic mt-0.5" data-id="element-7171" data-mp="20usdztpzr8ul">
                        {it.notes}
                      </div>}
                  </td>
                  <td className="text-right tabular-nums whitespace-nowrap pt-1.5" data-id="element-7172" data-mp="2ald8b4ymtr7p">
                    {fmt(it.line_total)}
                  </td>
                </tr>)}
            </tbody>
          </table>

          {/* Total — bold and visually dominant, matching the physical
           receipt's "Total ₡4,400.00" style. */}
          <div className="border-t-2 border-gray-900 pt-2 mt-2" data-id="element-7173" data-mp="2olo1wfrbtjwk">
            <div className="flex justify-between items-baseline" data-id="element-7174" data-mp="cz3xa9le8tdi">
              <span className="text-lg font-black" data-id="element-7175" data-mp="7z7uuescha02">Total</span>
              <span className="text-lg font-black tabular-nums" data-id="element-7176" data-mp="7xmzwo24bp1o">
                {fmt(payment.amount)}
              </span>
            </div>
          </div>

          {/* Payment metadata — Moneda + F. Pago */}
          <div className="border-t border-dashed border-gray-400 pt-2 mt-3 space-y-0.5 text-[11px]" data-id="element-7177" data-mp="3v5qmjw0500kz">
            <div className="flex justify-between" data-id="element-7178" data-mp="vqdaasudi09u">
              <span data-id="element-7179" data-mp="ro06i9z6scfp">Moneda</span>
              <strong data-id="element-7180" data-mp="rnqlz1wwuxhe">CRC</strong>
            </div>
            <div className="flex justify-between" data-id="element-7181" data-mp="mvfdvbs0o5dr">
              <span data-id="element-7182" data-mp="mu48j7ixapkj">F. Pago</span>
              <strong data-id="element-7183" data-mp="hxdxwzpw0m6i">{methodLabel}</strong>
            </div>
          </div>

          {/* Regime + footer note + legal disclaimer */}
          <p className="text-[11px] mt-3 font-medium" data-id="element-7184" data-mp="1fdb8fus9wcvf">{regimeLabel}</p>
          {info.footer_note && <p className="text-center text-[12px] mt-4 mb-1 font-medium text-gray-800" data-id="element-7185" data-mp="1ak8e3kfo8ze6">
              {info.footer_note}
            </p>}
          <p className="text-center text-[9px] mt-3 text-gray-500 leading-tight" data-id="element-7186" data-mp="1ygv8jvgnoffb">
            {info.regime === 'simplificado' ? 'Documento equivalente — no constituye factura electrónica.' : 'Documento autorizado por la DGT — Costa Rica.'}
          </p>
          <p className="text-center text-[9px] mt-1 text-gray-400" data-id="element-7187" data-mp="1ooyydm1aoyuq">
            Powered by NOIZA
          </p>
        </div>
      </div>
    </div>;
}